import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Image, Search, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Card, EmptyState, ErrorBanner, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { CARD_STATE_LABELS, formatDue, formatPercent } from "../lib/format";

const PAGE_SIZE = 25;

const STATE_FILTERS = [
    { value: "", label: "全部状态" },
    { value: "NEW", label: "未学" },
    { value: "LEARNING", label: "学习中" },
    { value: "REVIEW", label: "复习中" },
    { value: "RELEARNING", label: "重新学习" }
] as const;

const SCOPE_FILTERS = [
    { value: "", label: "全部" },
    { value: "FEDERAL", label: "联邦题 1–300" },
    { value: "STATE", label: "州题 301–310" }
] as const;

export function BrowsePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
    const search = searchParams.get("search") ?? "";
    const scope = searchParams.get("scope") ?? "";
    const state = searchParams.get("state") ?? "";
    const starred = searchParams.get("starred") === "true";
    const onlyImages = searchParams.get("onlyImages") === "true";

    // Local mirror of the search box so typing does not fire a request per keystroke.
    const [searchDraft, setSearchDraft] = useState(search);
    useEffect(() => setSearchDraft(search), [search]);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchDraft === search) return;
            updateParams({ search: searchDraft || undefined, page: undefined });
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft]);

    function updateParams(changes: Record<string, string | undefined>) {
        const next = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(changes)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
        }
        setSearchParams(next);
    }

    const listQuery = useQuery({
        queryKey: ["questions", { page, search, scope, state, starred, onlyImages }],
        queryFn: () =>
            api.questions.list({
                page,
                pageSize: PAGE_SIZE,
                ...(search ? { search } : {}),
                ...(scope ? { scope } : {}),
                ...(state ? { state } : {}),
                ...(starred ? { starred: true } : {}),
                ...(onlyImages ? { onlyImages: true } : {})
            }),
        placeholderData: keepPreviousData
    });

    const totalPages = listQuery.data ? Math.max(1, Math.ceil(listQuery.data.total / PAGE_SIZE)) : 1;

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl font-semibold text-ink">题库</h1>
                <p className="mt-1 text-sm text-ink-soft">
                    310 道官方题目，可以按题号、德语原文、中文词汇搜索。
                </p>
            </div>

            <Card className="space-y-3 p-4">
                <div className="relative">
                    <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-soft" />
                    <input
                        value={searchDraft}
                        onChange={(event) => setSearchDraft(event.target.value)}
                        placeholder="搜索题号、德语原文或词汇，例如 Grundgesetz、言论自由、21"
                        aria-label="搜索题目"
                        className="w-full rounded-xl border border-line bg-surface py-2.5 pr-3 pl-9 text-sm text-ink placeholder:text-ink-soft/60 focus:border-brand focus:outline-2 focus:outline-offset-1 focus:outline-brand"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={scope}
                        onChange={(event) => updateParams({ scope: event.target.value, page: undefined })}
                        className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-ink"
                    >
                        {SCOPE_FILTERS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <select
                        value={state}
                        onChange={(event) => updateParams({ state: event.target.value, page: undefined })}
                        className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-ink"
                    >
                        {STATE_FILTERS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    <button
                        onClick={() => updateParams({ starred: starred ? undefined : "true", page: undefined })}
                        className={cx(
                            "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition-colors",
                            starred ? "border-warn bg-warn-soft text-warn" : "border-line text-ink-soft"
                        )}
                    >
                        <Star className={cx("size-4", starred && "fill-warn")} aria-hidden />
                        收藏
                    </button>

                    <button
                        onClick={() =>
                            updateParams({ onlyImages: onlyImages ? undefined : "true", page: undefined })
                        }
                        className={cx(
                            "flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition-colors",
                            onlyImages ? "border-brand bg-brand-soft text-brand" : "border-line text-ink-soft"
                        )}
                    >
                        <Image className="size-4" aria-hidden />
                        图片题
                    </button>

                    {listQuery.data ? (
                        <span className="ml-auto text-sm text-ink-soft">共 {listQuery.data.total} 题</span>
                    ) : null}
                </div>
            </Card>

            {listQuery.isLoading ? <Spinner /> : null}
            {listQuery.isError ? (
                <ErrorBanner message="加载题库失败" onRetry={() => void listQuery.refetch()} />
            ) : null}

            {listQuery.data && listQuery.data.items.length === 0 ? (
                <EmptyState title="没有匹配的题目" description="换个关键词，或者把筛选条件清掉试试。" />
            ) : null}

            <ul className="space-y-2">
                {listQuery.data?.items.map(({ question, card }) => (
                    <li key={question.number}>
                        <Link
                            to={`/questions/${question.number}`}
                            className="flex items-start gap-3 rounded-xl border border-line bg-surface px-4 py-3 transition-colors hover:border-brand"
                        >
                            <span className="mt-0.5 w-10 shrink-0 text-sm text-ink-soft tabular-nums">
                                #{question.number}
                            </span>

                            <div className="min-w-0 flex-1 space-y-1.5">
                                <p lang="de" className="line-clamp-2 text-sm text-ink">
                                    {question.text}
                                </p>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {card ? (
                                        <Badge
                                            tone={
                                                card.state === "NEW"
                                                    ? "neutral"
                                                    : card.state === "REVIEW"
                                                      ? "good"
                                                      : "warn"
                                            }
                                        >
                                            {CARD_STATE_LABELS[card.state] ?? card.state}
                                        </Badge>
                                    ) : null}
                                    {question.scope === "STATE" ? (
                                        <Badge tone="brand">{question.state}</Badge>
                                    ) : null}
                                    {question.isImageQuestion ? <Badge>图片题</Badge> : null}
                                    {card && card.answerCount > 0 ? (
                                        <span className="text-xs text-ink-soft">
                                            正确率 {formatPercent(card.correctCount / card.answerCount)} ·{" "}
                                            {card.state === "NEW" ? "" : `${formatDue(card.due)}复习`}
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            {card?.isStarred ? (
                                <Star className="mt-0.5 size-4 shrink-0 fill-warn text-warn" aria-label="已收藏" />
                            ) : null}
                        </Link>
                    </li>
                ))}
            </ul>

            {totalPages > 1 ? (
                <nav className="flex items-center justify-center gap-3" aria-label="分页">
                    <button
                        disabled={page <= 1}
                        onClick={() => updateParams({ page: String(page - 1) })}
                        className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft disabled:opacity-40"
                    >
                        <ChevronLeft className="size-4" aria-hidden />
                        上一页
                    </button>
                    <span className="text-sm text-ink-soft tabular-nums">
                        {page} / {totalPages}
                    </span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => updateParams({ page: String(page + 1) })}
                        className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft disabled:opacity-40"
                    >
                        下一页
                        <ChevronRight className="size-4" aria-hidden />
                    </button>
                </nav>
            ) : null}
        </div>
    );
}
